import type { ForwardedRef, SyntheticEvent } from 'react';
import { forwardRef } from 'react';

import type { DialogProps, SxProps, Theme } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';

import { CloseIcon } from '../icons';
import Button from '../third_party/button';
import IconButton from '../third_party/icon-button';

import { StyledDialog, StyledDialogContent } from './styles';

type TRef = HTMLDivElement | null;

interface IProps extends DialogProps {
  onClose?: (e: boolean | 'YES' | 'NO') => void;
  disableBackdropClick?: boolean;
  title?: string;
  negativeButton?: { icon?: string | JSX.Element; loading?: boolean; disabled?: boolean; text: string };
  positiveButton?: { icon?: string | JSX.Element; loading?: boolean; disabled?: boolean; text: string };
  noScroll?: boolean;
  shouldManageWidth?: boolean;
  noPaperPadding?: boolean;
  showCloseButton?: boolean;
  width?: string;
  disableEscapeKeyDown?: boolean;
  dialogContentSX?: SxProps<Theme> | undefined;
}

function TempModal(
  {
    open = false,
    onClose,
    disableBackdropClick,
    title,
    children,
    negativeButton,
    positiveButton,
    noScroll,
    shouldManageWidth = false,
    noPaperPadding = false,
    showCloseButton = true,
    width = '608px',
    disableEscapeKeyDown = false,
    dialogContentSX,
  }: IProps,
  ref: ForwardedRef<TRef>
) {
  const handleCloseBtn = () => {
    if (onClose) onClose(false);
  };

  const closeModal = (_: SyntheticEvent, reason: string) => {
    if (disableBackdropClick && reason === 'backdropClick') return undefined;
    if (onClose) onClose(false);
    return undefined;
  };

  return (
    <StyledDialog
      disableEscapeKeyDown={disableEscapeKeyDown}
      open={Boolean(open)}
      onClose={closeModal}
      scroll={noScroll ? 'body' : 'paper'}
      shouldManageWidth={shouldManageWidth}
      noPaperPadding={noPaperPadding}
      width={width}
      ref={ref}
    >
      {showCloseButton && (
        <IconButton className="absolute right-2 top-2 h-11 w-11 md:right-3" onClick={handleCloseBtn} size="medium">
          <CloseIcon className="text-2xl" />
        </IconButton>
      )}

      {!!title && <DialogTitle>{title}</DialogTitle>}

      <StyledDialogContent sx={dialogContentSX} tabIndex={-1} noScroll={Boolean(noScroll)}>
        {children}
      </StyledDialogContent>

      {(positiveButton || negativeButton) && (
        <DialogActions className="mb-4 flex flex-col px-4 md:flex-row md:justify-start md:px-8">
          {negativeButton && (
            <Button
              className="mx-2 mb-2 min-w-full md:mx-0 md:mb-0 md:min-w-0"
              startIcon={negativeButton.icon}
              fullWidth={!positiveButton}
              onClick={() => {
                if (onClose) {
                  onClose('NO');
                }
              }}
              loading={negativeButton.loading}
              disabled={negativeButton.disabled}
            >
              {negativeButton.text}
            </Button>
          )}

          {positiveButton && (
            <Button
              variant="contained"
              loading={positiveButton.loading}
              disabled={positiveButton.disabled}
              className="mx-2 min-w-full md:mx-0 md:min-w-0"
              fullWidth={!negativeButton}
              startIcon={positiveButton.icon}
              onClick={() => {
                if (onClose) {
                  onClose('YES');
                }
              }}
            >
              {positiveButton.text}
            </Button>
          )}
        </DialogActions>
      )}
    </StyledDialog>
  );
}

const Modal = forwardRef<TRef, IProps>(TempModal);

export default Modal;
